import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import ContactModel from 'donor-elf-web/models/contact';
import NewsletterContactModel from 'donor-elf-web/models/newsletter-contact';
import { type NewsletterRouteModel } from 'donor-elf-web/routes/funds/fund/more/newsletter';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import FundsService from 'donor-elf-web/services/funds-service';

class SelectedNewsletterContact {
  constructor(args: {
    contact: ContactModel;
    newsletterContact?: NewsletterContactModel;
    selected: boolean;
  }) {
    this.contact = args.contact;
    this.newsletterContact = args.newsletterContact;
    this.selected = args.selected;
  }

  contact: ContactModel;
  newsletterContact?: NewsletterContactModel;
  @tracked selected: boolean;
  // used when the user tries to select a contact without all their address fields filled out
  @tracked showCantSelectContactError: boolean = false;

  get contactIsMissingAddressFields() {
    return (
      isEmpty(this.contact.address1) ||
      isEmpty(this.contact.city) ||
      isEmpty(this.contact.state) ||
      isEmpty(this.contact.zip)
    );
  }

  get changeNotSavedYet() {
    return (
      (this.selected && this.newsletterContact == null) ||
      (!this.selected && this.newsletterContact != null)
    );
  }
}

export default class NewsletterConnectedController extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare fundsService: FundsService;
  @service declare router: RouterService;
  @service declare store: Store;

  model!: NewsletterRouteModel;

  @tracked loading = false;
  @tracked saving = false;
  @tracked saved = false;
  @tracked error?: any;
  @tracked newsletterContactsByContactId: {
    [contactId: string]: NewsletterContactModel;
  } = {};
  @tracked selectedNewsletterContacts?: SelectedNewsletterContact[];
  @tracked nameFilter: string = '';
  @tracked groupFilter: string = '';

  get filterApplied() {
    return this.nameFilter.length > 2 || this.groupFilter.length > 0;
  }

  get subscribedContactsCount() {
    return (this.selectedNewsletterContacts?.filter((x) => x.selected) || [])
      .length;
  }

  get filteredSelectedNewsletterContacts() {
    let filteredItems = this.selectedNewsletterContacts || [];
    if (this.filterApplied) {
      if (this.nameFilter.length > 2) {
        let filteredContactIds: any = {};
        this.contactsCache
          .search(this.nameFilter)
          .forEach((x: ContactModel) => {
            filteredContactIds[x.id] = true;
          });
        filteredItems = filteredItems.filter((x) => {
          return filteredContactIds[x.contact.id] || false;
        });
      }
      if (this.groupFilter.length > 0) {
        let groupContactIds: any = {};
        this.contactsCache.groupContacts[this.groupFilter].contacts.forEach(
          (x) => {
            groupContactIds[x.id] = true;
          },
        );
        filteredItems = filteredItems.filter(
          (x) => groupContactIds[x.contact.id],
        );
      }
    }
    return filteredItems;
  }

  get allFilteredSelectedNewsletterContactsSelected() {
    return this.filteredSelectedNewsletterContacts
      .filter((x) => !x.contactIsMissingAddressFields)
      .every((x) => x.selected);
  }

  get availableGroupOptions() {
    return [
      {
        val: '',
        label: 'Filter by Group',
      },
    ].concat(
      this.contactsCache.sortedGroups.map((x: string) => {
        return { val: x, label: x };
      }),
    );
  }

  @action
  templateDidInsert() {
    this.loadNewsletterContacts();
  }

  @action
  templateWillDestroy() {
    this.loading = false;
    this.saving = false;
    this.saved = false;
    this.error = undefined;
    this.newsletterContactsByContactId = {};
    this.selectedNewsletterContacts = undefined;
    this.nameFilter = '';
    this.groupFilter = '';
  }

  @action
  async loadNewsletterContacts() {
    this.loading = true;
    this.newsletterContactsByContactId = {};
    this.selectedNewsletterContacts = undefined;
    (
      await queryWithPages<NewsletterContactModel>({
        store: this.store,
        modelName: 'newsletter-contact',
        queryOptions: {
          filter: {
            newsletter_account_id: this.model.newsletterAccount!.id,
          },
        },
        pageSize: 500,
      })
    ).forEach((x) => (this.newsletterContactsByContactId[x.contactId!] = x));

    this.selectedNewsletterContacts = this.contactsCache.sortedContacts.map(
      (contact) => {
        let newsletterContact = this.newsletterContactsByContactId[contact.id];
        return new SelectedNewsletterContact({
          contact,
          newsletterContact,
          selected: newsletterContact != null,
        });
      },
    );
    this.sortTheSelectedNewsletterContacts();

    this.loading = false;
  }

  @action
  handleSelectButtonClicked() {
    let checkedValue = this.allFilteredSelectedNewsletterContactsSelected
      ? false
      : true;
    this.filteredSelectedNewsletterContacts
      .filter((x) => !x.contactIsMissingAddressFields)
      .forEach((item) => {
        item.selected = checkedValue;
      });
  }

  @action
  handleNameFilterInput(e: any) {
    this.nameFilter = e.target.value;
    if (!this.filterApplied) {
      this.sortTheSelectedNewsletterContacts();
    }
  }

  @action
  handleGroupChange(e: any) {
    this.groupFilter = e.target.value;
    if (!this.filterApplied) {
      this.sortTheSelectedNewsletterContacts();
    }
  }

  @action
  handleGroupClicked(group: string) {
    this.nameFilter = '';
    this.groupFilter = group;
  }

  @action
  toggleSelected(item: SelectedNewsletterContact) {
    if (!item.selected && item.contactIsMissingAddressFields) {
      // prevent the user from selecting a contact with missing address fields and show an error
      item.showCantSelectContactError = !item.showCantSelectContactError;
    } else {
      item.selected = !item.selected;
    }
  }

  sortTheSelectedNewsletterContacts() {
    this.selectedNewsletterContacts = sortByProperties(
      this.selectedNewsletterContacts,
      ['selected:desc', 'name:asc'],
    );
  }

  @action
  async saveChanges() {
    if (this.saving) {
      return;
    }
    this.saving = true;
    let saveStartedAt = new Date();
    try {
      let record = this.store.createRecord('newsletter-contact-bulk-update', {
        newsletterAccountId: this.model.newsletterAccount!.id,
        contactIds: this.selectedNewsletterContacts!.filter(
          (x) => x.selected,
        ).map((x) => x.contact.id),
      });
      await record.save();
      await this.loadNewsletterContacts();
      let saveTime = new Date() - saveStartedAt;
      if (saveTime < 1500) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      this.saving = false;
      this.saved = true;
    } catch (e) {
      this.error = e;
    }
  }
}
